import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api/client';

/* ───────────────────────────────────────────────────────────────────────────
   Contrato — GET /fiscal/assessment-report (FiscalAnalyticsController)

   Espelha `AssessmentReportResult` (application/queries/get-assessment-report
   .query.ts). O DTO da apresentação (`AssessmentReportResponseDto`) declara
   `data`/`summary` como `Record<string, unknown>`; o query de verdade devolve
   as interfaces abaixo.

   Query params (AssessmentReportQueryDto):
     - companyId?, branchId?, type? (NFE|NFCE|NFSE), cfop?, competencia?
       (YYYY-MM), startDate?, endDate?, page (padrão 1), limit (padrão 50,
       máx 200)

   O tenant vem da sessão.
   ─────────────────────────────────────────────────────────────────────────── */

export interface AssessmentByTaxType {
  taxType: string;
  label: string;
  base: number;
  value: number;
  aliquotaMediaPct: number;
}

export interface AssessmentReportLine {
  id: string;
  tipo: string;
  numero: string | null;
  serie: string | null;
  dataEmissao: string;
  cfop: string;
  baseCalculo: number;
  aliquotaMediaPct: number;
  valorImposto: number;
  status: string;
  partnerName: string | null;
  byTaxType: Record<string, { base: number; value: number; aliquota: number }>;
}

export interface AssessmentReportSummary {
  totalDocuments: number;
  totalBaseCalculo: number;
  totalImposto: number;
  aliquotaMediaPct: number;
  byTaxType: AssessmentByTaxType[];
}

export interface AssessmentReport {
  data: AssessmentReportLine[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  summary: AssessmentReportSummary;
}

export interface AssessmentReportQuery {
  type?: string;
  cfop?: string;
  competencia?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export function useAssessmentReport(params?: AssessmentReportQuery) {
  return useQuery({
    queryKey: ['fiscal', 'assessment-report', params] as const,
    queryFn: () =>
      apiClient.get<AssessmentReport>('/fiscal/assessment-report', {
        params: {
          page: String(params?.page ?? 1),
          limit: String(params?.limit ?? 50),
          ...(params?.type && params.type !== 'ALL' ? { type: params.type } : {}),
          ...(params?.cfop ? { cfop: params.cfop } : {}),
          ...(params?.competencia ? { competencia: params.competencia } : {}),
          ...(params?.startDate ? { startDate: params.startDate } : {}),
          ...(params?.endDate ? { endDate: params.endDate } : {}),
        },
      }),
  });
}